"use client";

import React, { useState, useEffect } from "react";
import { motion } from 'framer-motion';
import { getBackendRenderAttributes, isBackendRenderMode } from "@/lib/backend-render-utils";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import CustomerApiService, { type ProfileUpdateData, type BillingAddress } from "@/services/CustomerApiService";
import { useCustomerPanelTranslations } from "@/lib/customer-panel-utils";
import { ErrorMessage } from "@/lib/customer-panel/ui-components";
import { fadeInVariants, cardVariants } from "@/lib/animations";
import PersonalInfoForm from "@/components/customer/profile/PersonalInfoForm";
import PasswordForm from "@/components/customer/profile/PasswordForm";
import BillingAddressesList from "@/components/customer/profile/BillingAddressesList";
import { useCountries } from "@/contexts/CountriesContext";
import type { Country } from "@/contexts/CountriesContext";
import type { PageUrlMap } from "@/types/blog";

interface CustomerProfileProps {
  language?: string;
  languageId?: number;
  translations?: Record<string, string>;
  pageUrls?: PageUrlMap;
  defaultLanguage?: string;
  countries?: Country[];
  __grapesPreview?: boolean;
}

export default function CustomerProfile({
  language = 'en',
  languageId,
  translations = {},
  pageUrls = {},
  defaultLanguage = 'en',
  countries: countriesProp,
  __grapesPreview = false,
  ...restProps
}: CustomerProfileProps) {
  const countriesFromContext = useCountries();
  const countries = (countriesProp && countriesProp.length > 0) ? countriesProp : countriesFromContext;

  const isBackendRender = isBackendRenderMode({ __grapesPreview, ...restProps });
  const backendAttributes = getBackendRenderAttributes(
    'page_layouts/CustomerProfile',
    { language, translations, pageUrls },
    isBackendRender
  );

  const t = useCustomerPanelTranslations(translations);
  const { customer, refreshCustomer, isAuthenticated, isLoading: authLoading } = useCustomerAuth();
  
  const displayCustomer = isBackendRender ? {
    id: 1,
    first_name: 'John',
    last_name: 'Doe',
    email: 'john.doe@example.com',
    phone: '+90 555 123 4567',
    email_verified_at: new Date().toISOString(),
    phone_verified_at: null,
    newsletter_subscribed: false,
    account_status: 'active',
  } : customer;
  
  const [activeTab, setActiveTab] = useState<'personal' | 'password' | 'billing'>('personal');
  const [billingAddresses, setBillingAddresses] = useState<BillingAddress[]>([]);
  const [billingAddressesLoading, setBillingAddressesLoading] = useState(false);

  const handleProfileUpdate = async (data: ProfileUpdateData) => {
    if (!displayCustomer || !refreshCustomer || isBackendRender) return;
    await CustomerApiService.updateProfile(data);
    await refreshCustomer();
  };

  if (!isBackendRender && (!isAuthenticated || !customer) && !authLoading) {
    return null;
  }

  if (!displayCustomer) {
    return (
      <div {...backendAttributes} className="container mx-auto px-4 py-8">
        <ErrorMessage message={t('customer_panel.common.error', 'An error occurred')} />
      </div>
    );
  }

  return (
    <motion.div 
      {...backendAttributes}
      variants={fadeInVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div 
        className="mb-8"
        variants={fadeInVariants}
        initial="hidden"
        animate="visible"
      >
        <h1 className="text-3xl font-bold text-gray-900">
          {t('customer_panel.profile.title', 'My Profile')}
        </h1>
      </motion.div>

      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8" role="tablist" aria-label={t('customer_panel.profile.tabs', 'Profile tabs')}>
          <button type="button"
            onClick={() => setActiveTab('personal')}
            role="tab"
            aria-selected={activeTab === 'personal'}
            aria-controls="personal-tabpanel"
            id="personal-tab"
            className={`
              py-4 px-1 border-b-2 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-alsero-first focus:ring-offset-2 transition-colors
              ${activeTab === 'personal'
                ? 'border-alsero-first text-alsero-first'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            {t('customer_panel.profile.personal_info', 'Personal Information')}
          </button>
          <button type="button"
            onClick={() => setActiveTab('password')}
            role="tab"
            aria-selected={activeTab === 'password'}
            aria-controls="password-tabpanel"
            id="password-tab"
            className={`
              py-4 px-1 border-b-2 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-alsero-first focus:ring-offset-2 transition-colors
              ${activeTab === 'password'
                ? 'border-alsero-first text-alsero-first'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            {t('customer_panel.profile.change_password', 'Change Password')}
          </button>
          <button type="button"
            onClick={() => setActiveTab('billing')}
            role="tab"
            aria-selected={activeTab === 'billing'}
            aria-controls="billing-tabpanel"
            id="billing-tab"
            className={`
              py-4 px-1 border-b-2 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-alsero-first focus:ring-offset-2 transition-colors
              ${activeTab === 'billing'
                ? 'border-alsero-first text-alsero-first'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            {t('customer_panel.billing.title', 'Billing Addresses')}
          </button>
        </nav>
      </div>

      <motion.div 
        className="bg-white rounded-lg drop-shadow-card border border-gray-200 p-6 group hover:shadow-2xl transition-all duration-300"
        variants={cardVariants}
        initial="hidden"
        animate="visible"
      >
        {activeTab === 'personal' && (
          <div role="tabpanel" id="personal-tabpanel" aria-labelledby="personal-tab">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              {t('customer_panel.profile.personal_info', 'Personal Information')}
            </h2>
            
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      {t('customer_panel.profile.email', 'Email')}
                    </p>
                    <p className="text-sm text-gray-600">{displayCustomer.email}</p>
                  </div>
                  {displayCustomer.email_verified_at ? (
                    <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                      {t('customer_panel.profile.email_verified', 'Email Verified')}
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                      {t('customer_panel.profile.email_not_verified', 'Email Not Verified')}
                    </span>
                  )}
                </div>
                
                {displayCustomer.phone && (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        {t('customer_panel.profile.phone', 'Phone')}
                      </p>
                      <p className="text-sm text-gray-600">{displayCustomer.phone}</p>
                    </div>
                    {displayCustomer.phone_verified_at ? (
                      <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                        {t('customer_panel.profile.phone_verified', 'Phone Verified')}
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                        {t('customer_panel.profile.phone_not_verified', 'Phone Not Verified')}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            <PersonalInfoForm
              customer={displayCustomer}
              onUpdate={handleProfileUpdate}
              translations={translations}
              countries={countries}
            />
          </div>
        )}

        {activeTab === 'password' && (
          <div role="tabpanel" id="password-tabpanel" aria-labelledby="password-tab">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              {t('customer_panel.profile.change_password', 'Change Password')}
            </h2>
            <PasswordForm translations={translations} onSuccess={refreshCustomer} />
          </div>
        )}

        {activeTab === 'billing' && (
          <div role="tabpanel" id="billing-tabpanel" aria-labelledby="billing-tab">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              {t('customer_panel.billing.title', 'Billing Addresses')}
            </h2>
            {billingAddressesLoading ? (
              <div className="text-center py-8">
                <p className="text-gray-600">{t('customer_panel.common.loading', 'Loading...')}</p>
              </div>
            ) : (
              <BillingAddressesList
                addresses={billingAddresses}
                onAdd={async (data: BillingAddress) => {
                  const newAddress = await CustomerApiService.createBillingAddress(data);
                  setBillingAddresses(prev => [...prev, newAddress]);
                }}
                onEdit={async (id: number, data: BillingAddress) => {
                  const updatedAddress = await CustomerApiService.updateBillingAddress(id, data);
                  setBillingAddresses(prev => prev.map(addr => addr.id === id ? updatedAddress : addr));
                }}
                onDelete={async (id: number) => {
                  await CustomerApiService.deleteBillingAddress(id);
                  setBillingAddresses(prev => prev.filter(addr => addr.id !== id));
                }}
                onSetDefault={async (id: number) => {
                  await CustomerApiService.setDefaultBillingAddress(id);
                  setBillingAddresses(prev => prev.map(addr => ({
                    ...addr,
                    is_default: addr.id === id
                  })));
                }}
                translations={translations}
                countries={countries}
              />
            )}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
